import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default api;

// API helper functions
export const companiesApi = {
  getAll: () => api.get('/companies'),
  get: (id) => api.get(`/companies/${id}`),
  create: (data) => api.post('/companies', data),
  update: (id, data) => api.put(`/companies/${id}`, data),
  delete: (id) => api.delete(`/companies/${id}`),
};

export const employeesApi = {
  getAll: (params) => api.get('/employees', { params }),
  get: (id) => api.get(`/employees/${id}`),
  create: (data) => api.post('/employees', data),
  update: (id, data) => api.put(`/employees/${id}`, data),
  delete: (id) => api.delete(`/employees/${id}`),
};

export const censusApi = {
  preview: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    // Don't set Content-Type - browser will set it with boundary automatically
    return api.post('/census/preview', formData, {
      headers: { 'Content-Type': undefined },
    });
  },
  compare: (file, companyId, columnMapping) => {
    const formData = new FormData();
    formData.append('file', file);
    // Don't set Content-Type - browser will set it with boundary automatically
    return api.post('/census/compare', formData, {
      headers: { 'Content-Type': undefined },
      params: {
        company_id: companyId,
        column_mapping: JSON.stringify(columnMapping),
      },
    });
  },
  initialUpload: (file, companyId, columnMapping) => {
    const formData = new FormData();
    formData.append('file', file);
    // Don't set Content-Type - browser will set it with boundary automatically
    return api.post('/census/initial-upload', formData, {
      headers: { 'Content-Type': undefined },
      params: {
        company_id: companyId,
        column_mapping: JSON.stringify(columnMapping),
      },
    });
  },
  apply: (companyId, addNew, markQuit, updateAddresses, newEmployees, quitEmployeeIds, updatedEmployees) => {
    return api.post('/census/apply', {
      company_id: companyId,
      add_new: addNew,
      mark_quit: markQuit,
      update_addresses: updateAddresses,
      new_employees: newEmployees,
      quit_employee_ids: quitEmployeeIds,
      updated_employees: updatedEmployees || [],
    });
  },
};

export const businessStatesApi = {
  getAll: (activeOnly = false) => api.get('/business-states', { params: { active_only: activeOnly } }),
  create: (data) => api.post('/business-states', data),
  update: (id, isActive) => api.put(`/business-states/${id}`, null, { params: { is_active: isActive } }),
  delete: (id) => api.delete(`/business-states/${id}`),
};

export const reportsApi = {
  getTenure: (companyId) => api.get('/reports/tenure', { params: { company_id: companyId } }),
  getStatesDistribution: (companyId) => api.get('/reports/states-distribution', { params: { company_id: companyId } }),
  getDashboardStats: () => api.get('/reports/dashboard-stats'),
};

export const exportApi = {
  mailingList: (params) => {
    return api.get('/export/mailing-list', {
      params,
      responseType: 'blob',
    });
  },
  tenureReport: (companyId) => {
    return api.get('/export/tenure-report', {
      params: { company_id: companyId },
      responseType: 'blob',
    });
  },
  verificationReport: (companyId) => {
    return api.get('/export/verification-report', {
      params: { company_id: companyId },
      responseType: 'blob',
    });
  },
};
